import React, { useState } from 'react';
import { CreditCard, AlertCircle, CheckCircle, Lock, Tag } from 'lucide-react';
import { Modal, ModalFooter } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/useAuth';
import { analytics } from '@/utils/analytics';
import { supabase } from '@/utils/supabase';
import { metaPixel } from '@/utils/meta-pixel';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  creditsAmount: number;
  price: number;
  dpeId?: string;
  onSuccess?: () => void;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  creditsAmount,
  price,
  dpeId,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const { user, refreshUserProfile, refreshUserCredits } = useAuth();

  const handleApplyPromo = () => {
    if (!promoCode.trim()) return;

    setPromoApplied(true);
    setError(null);

    analytics.trackEvent({
      category: 'payment',
      action: 'apply_promo',
      label: promoCode
    });
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoApplied(false);
  };

  const handlePayment = async () => {
    try {
      setLoading(true);
      setError(null);

      analytics.trackEvent({
        action: 'start',
        category: 'payment',
        label: dpeId ? `credit_for_dpe_${dpeId}` : 'credit_purchase',
        value: price,
        custom_parameters: {
          credits_amount: creditsAmount,
          price: price,
          dpe_id: dpeId,
          promo_code: promoApplied ? promoCode : null
        }
      });

      metaPixel.trackEvent('InitiateCheckout', {
        content_name: 'DPE Credit Selection',
        content_category: 'credits',
        value: price,
        currency: 'EUR',
        contents: [{
          id: 'dpe_credits',
          quantity: creditsAmount,
          item_price: price / creditsAmount
        }],
        custom_data: {
          credits_amount: creditsAmount
        }
      });

      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/success?session_id={CHECKOUT_SESSION_ID}&credits=${creditsAmount}&type=credit`;
      const cancelUrl = window.location.href;

      const response = await fetch('/.netlify/functions/payment-one-time-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || ''}`
        },
        body: JSON.stringify({
          credits_amount: creditsAmount,
          success_url: successUrl,
          cancel_url: cancelUrl,
          // ✅ CORRECTION : Transmission correcte du code promo
          promo_code: promoCode.trim() !== '' ? promoCode : undefined,
          // ✅ NOUVEAU : Données utilisateur pour pré-remplissage
          customer_email: user?.email,
          customer_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : undefined,
          user_id: user?.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la création de la session de paiement');
      }

      const data = await response.json();

      window.location.href = data.checkout_url;
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message || 'Une erreur est survenue lors du paiement');
      setLoading(false);

      analytics.trackError(err, 'payment_modal');
      analytics.trackEvent({
        action: 'error',
        category: 'payment',
        label: 'payment_creation',
        custom_parameters: {
          error_message: err.message
        }
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={success ? 'Paiement confirmé' : 'Acheter un crédit'}
      description={success ? 'Votre crédit a été ajouté à votre compte' : `Achetez ${creditsAmount} crédit${creditsAmount > 1 ? 's' : ''} pour ${price}€`}
    >
      {success ? (
        <div className="text-center py-6">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Paiement confirmé !
          </h3>
          <p className="text-gray-600 mb-6">
            {creditsAmount} crédit{creditsAmount > 1 ? 's ont été ajoutés' : ' a été ajouté'} à votre compte.
          </p>
          <Button
            variant="primary"
            onClick={() => {
              onSuccess?.();
              onClose();
            }}
          >
            Continuer
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-6 py-4">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <Card className="p-4 bg-blue-50 border border-blue-100">
              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="w-full">
                  <h4 className="font-medium text-blue-800 mb-3">Détails de la commande</h4>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li className="flex justify-between">
                      <span>{creditsAmount} crédit{creditsAmount > 1 ? 's' : ''}</span>
                      <span className="font-medium">{price.toFixed(2)}€</span>
                    </li>
                    {promoApplied && (
                      <li className="flex justify-between text-green-600">
                        <span>Code promo "{promoCode}"</span>
                        <span>✓ Appliqué</span>
                      </li>
                    )}
                    <li className="flex justify-between border-t border-blue-200 pt-2 mt-2 font-bold text-blue-800">
                      <span>Prix total</span>
                      <span>{price.toFixed(2)}€</span>
                    </li>
                    {promoApplied && (
                      <li className="text-xs text-green-600 italic">
                        * La réduction sera appliquée par Stripe lors du paiement
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </Card>

            {/* Code promo */}
            {!promoApplied ? (
              <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <Input
                    id="promoCode"
                    placeholder="Code promo (optionnel)"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    disabled={loading}
                    leftIcon={<Tag className="w-4 h-4" />}
                    onKeyPress={(e) => e.key === 'Enter' && handleApplyPromo()}
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleApplyPromo}
                  disabled={loading || !promoCode.trim()}
                >
                  Appliquer
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-700">Code "{promoCode}" sera appliqué par Stripe</span>
                </div>
                <Button variant="ghost" size="sm" onClick={handleRemovePromo}>
                  Retirer
                </Button>
              </div>
            )}

            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Lock className="w-4 h-4" />
              <span>Paiement sécurisé via Stripe</span>
            </div>
          </div>

          <ModalFooter>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              onClick={handlePayment}
              loading={loading}
            >
              {promoApplied ? 'Payer avec code promo' : `Payer ${price.toFixed(2)}€`}
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default PaymentModal;
